import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@carbon/react';
import { Column, Renew, Table } from '@carbon/react/icons';
import { definePage, defineResource, ListQuery, TenantParams } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import FormattedDate from 'src/components/Base/FormattedDate';
import IssueBoard from 'src/components/Issue/IssueBoard';
import IssueForm from 'src/components/Issue/IssueForm';
import { IssueStatusTag, IssueTypeTag } from 'src/components/Issue/IssueTags';
import { ALL_BRANCHES } from 'src/context/types';
import { useTenantContext } from 'src/context/useTenantContext';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { useUrlSearchFilter } from 'src/hooks/useUrlSearchFilter';
import {
  orgRestaurantBranches,
  tenantEmployees,
  tenantIssueCreate,
  tenantIssueDelete,
  tenantIssues,
  tenantIssueUpdate,
} from 'src/services/apis/tenantRequests';
import { BranchScope, RestaurantScope } from 'src/services/apis/tenantRequests';
import { IssueEntity } from 'src/services/apis/types';
import { employeeName } from 'src/utils/employee';
import { issueStatusOptions, IssueTypeEnum, IssueTypes } from 'src/utils/issue';
import { QUERIES } from 'src/utils/react-query';

/** The branch column's name source — the same org list useBranches hands the old cell. */
const branchReference = (params: TenantParams) => orgRestaurantBranches(params);
/** The employee filter's name source, in the shape employees.tsx already uses. */
const employeeReference = (params: TenantParams) => tenantEmployees(params as RestaurantScope);

/**
 * The AllIssues page config, as a hook because the route-derived issue type is page state the
 * chip vocabulary cannot express: it is injected as a server-side `type||$eq` filter (the old
 * hook did the same; it was never a visible control).
 *
 * The date range used to be a second piece, a picker in the toolbar, because the between chip
 * sent raw midnight bounds and lost the last day. `compileFilter` now bounds a date between
 * end-of-day inclusive, so it is an ordinary `createdAt` chip in the filter popover.
 *
 * The palette deep-link's `?search=` term bridges into the same `title||$contL` filter
 * the old hook seeded its server-side search from, until the crud search box takes over.
 */
export const useIssuesPage = ({
  employee,
  dish,
  operation,
}: {
  employee?: boolean;
  dish?: boolean;
  operation?: boolean;
}) => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const urlSearch = useUrlSearchFilter();
  // The capability name, not `!isMember`: useAuthPermissions' own doc comment says call sites
  // branch on capabilities because the role-to-capability mapping is allowed to move. It
  // resolves to owner ‖ admin ‖ platform admin — exactly the server's
  // @RequireOrgRole('owner', 'admin') on every Issues write route.
  const { canManageRestaurant } = useAuthPermissions();
  // Read from tenant context, NOT from useCurrentRestaurantBranch(): that hook resolves the
  // branch out of the fetched branch list and returns null while the list is still loading
  // (useRestaurantBranches.ts:22-29), which would disable drag for the first render of every
  // concrete-branch board. The URL segment is synchronous and is what the server checks.
  const { branchId } = useTenantContext();
  const isSingleBranch = Boolean(branchId) && branchId !== ALL_BRANCHES;

  const issueType: IssueTypeEnum | null = employee
    ? IssueTypeEnum.EMPLOYEE
    : dish
    ? IssueTypeEnum.DISH
    : operation
    ? IssueTypeEnum.OPERATION
    : null;

  const issueTitle = employee
    ? t('common.employeeIssue')
    : dish
    ? t('common.dishIssue')
    : operation
    ? t('common.operationIssue')
    : t('common.issue');

  // The page heading needs the plural, and it has to come from the bundle rather than from
  // `${issueTitle}s`: Vietnamese has no plural -s, so the concatenation rendered "Vấn đềs".
  const issueTitlePlural = employee
    ? t('common.employeeIssues')
    : dish
    ? t('common.dishIssues')
    : operation
    ? t('common.operationIssues')
    : t('common.issues');

  const issues = useMemo(
    () =>
      defineResource({
        key: QUERIES.ISSUES,
        // The hand-written hook sent limit 20 and drove server page/limit from the
        // pagination bar (page sizes 20/50/100) — the crud layer's pagination is the same
        // machine.
        pageSize: 20,
        api: {
          list: (scope: BranchScope, query?: ListQuery) => {
            const injected: string[] = [];
            if (issueType != null) {
              injected.push(`type||$eq||${issueType}`);
            }
            if (urlSearch && !query?.s) {
              injected.push(`title||$contL||${urlSearch}`);
            }
            return tenantIssues(scope, { ...query, filter: [...(query?.filter ?? []), ...injected] });
          },
          create: tenantIssueCreate,
          update: tenantIssueUpdate,
          remove: tenantIssueDelete,
        },
        // The hand-written page searched title server-side (debounced $contL filter).
        search: 'server',
        searchFields: ['title'],
        filterable: [
          {
            field: 'status',
            labelKey: 'common.status',
            type: 'enum',
            options: issueStatusOptions.map((option) => ({ id: String(option.id), labelKey: option.text })),
          },
          // Only on the All tab. On a typed tab the route already injects `type||$eq`, and a
          // chip there would let the user contradict the tab they are standing on.
          ...(issueType === null
            ? [
                {
                  field: 'type',
                  labelKey: 'common.type',
                  type: 'enum' as const,
                  options: IssueTypes.map((option) => ({ id: String(option.value), labelKey: option.label })),
                },
              ]
            : []),
          {
            field: 'employeeId',
            labelKey: 'common.employee',
            type: 'reference',
            load: employeeReference,
            optionLabel: (employee: any) => `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim(),
          },
          { field: 'createdAt', labelKey: 'common.openedAt', type: 'date' },
        ],
      }),
    [issueType, urlSearch]
  );

  // The page state above cannot ride the query key (it is not a crud chip), so the
  // narrowed list is fetched explicitly once the render that captured it has committed.
  const mountedRef = useRef(false);
  const refetchRef = useRef<null | (() => void)>(null);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    refetchRef.current?.();
    // issueType rides the route: switching tabs reuses this component instance with an
    // unchanged crud query key, so without it here the previous route's rows would stay.
  }, [urlSearch, issueType]);

  const page = {
    ...definePage(issues, {
      titleKey: currentRestaurantBranch?.name ? 'titles.issuesPage' : 'titles.issuesPageWithoutBranch',
      // The old description was `${total} ${title.toLowerCase()}` — the singular keys
      // preserve that lowercase copy byte-for-byte.
      descriptionKey: employee
        ? 'common.employeeIssueSingular'
        : dish
        ? 'common.dishIssueSingular'
        : operation
        ? 'common.operationIssueSingular'
        : 'common.issueSingular',
      // "Create New Issue" — the modal heading's exact pre-migration copy
      // (createTitled had dropped the "New"; the vi key interpolates the same way).
      createLabelKey: 'actions.createNewWithTitle',
      updateLabelKey: 'actions.updateTitled',
      deleteHeadingKey: 'messages.areYouSureToDeleteThis',
      columns: [
        // The column a user identifies an issue by must survive every prefs
        // reconciliation; the wrapper passes the query through, so the server can sort it.
        { key: 'title', headerKey: 'common.title', sortable: true, alwaysVisible: true },
        // The server eagerly joins employee + user, so the name comes from the row itself.
        // The literal 'N/A' fallback is preserved verbatim, wart included.
        {
          key: 'employee',
          headerKey: 'common.employee',
          // The old cell wrote a literal 'N/A', untranslated, and did so on the dish and
          // operation tabs where an employee is never expected. The column stays on all four
          // tabs rather than being dropped per-tab: prefs are stored under one resourceKey
          // shared by every tab, and reconcileColumnPrefs strips keys absent from the config,
          // so a per-tab column would silently reset the user's hide and order choices each
          // time they crossed tabs.
          render: (row: IssueEntity) => employeeName(row.employee) || t('common.noEmployee'),
        },
        // currency renders via the portal's useMoney formatter — the same one the hand-written
        // cell used, minus the empty-string gap it left whenever fine was missing.
        { key: 'fine', headerKey: 'common.fine', display: { kind: 'currency' } },
        // The tag components carry the old cells' exact colors and labels — ported verbatim.
        { key: 'type', headerKey: 'common.type', render: (row: IssueEntity) => <IssueTypeTag type={row.type} /> },
        {
          key: 'status',
          headerKey: 'common.status',
          render: (row: IssueEntity) => <IssueStatusTag status={row.status} />,
        },
        {
          key: 'restaurantBranchId',
          headerKey: 'common.branch',
          display: {
            kind: 'reference',
            load: branchReference,
            optionLabel: (branch: any) => branch.name,
          },
        },
        // The old cell showed Today for the current day before any fixed format applied —
        // the declarative date kind has no such case, so the component stays.
        {
          key: 'createdAt',
          headerKey: 'common.openedAt',
          render: (row: IssueEntity) => <FormattedDate date={row.createdAt} format={'DD/MM/YYYY'} />,
        },
      ],
      form: IssueForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => {
        // The hand-written handler returned early here: nothing was submitted and the modal
        // stayed open. Throwing keeps both of those properties — the modal only closes when
        // the mutations behind it resolve — so the guard survives the seam verbatim.
        if (values.type === IssueTypeEnum.EMPLOYEE && !values.employeeId) {
          throw new Error(t('messages.employeeIssueNeedsEmployee'));
        }
        return {
          ...values,
          restaurantId: params.restaurantId,
          tags: values.tags?.split(',').map((tag: string) => tag.trim()) || [],
          fine: Number(values.fine || 0),
        };
      },
      useTitleVars: () => ({
        restaurantName: currentRestaurant?.name,
        branchName: currentRestaurantBranch?.name,
        title: issueTitle,
        titlePlural: issueTitlePlural,
      }),
    }),
    canWrite: canManageRestaurant,
    views: [
      { key: 'table', labelKey: 'views.table', icon: Table },
      {
        key: 'board',
        labelKey: 'views.board',
        icon: Column,
        // `issues.api.list` rather than tenantIssues: the route's injected `type||$eq` lives in
        // that closure, so going through it is what keeps the
        // board agreeing with the tab it is rendered on.
        //
        // The position endpoint refuses a request with no concrete branch — a rank is defined
        // inside one (branch, status) lane — so the all-branches board is handed a canWrite of
        // false and never offers a drag the server would reject.
        render: (context: any) => (
          <IssueBoard context={{ ...context, canWrite: context.canWrite && isSingleBranch }} list={issues.api.list} />
        ),
      },
    ],
    toolbar: (state: { refetch: () => void }) => {
      // Same propagation path as transactions.tsx: page state (the route's issue type, the
      // palette's search) cannot ride the crud query key, so the toolbar hands the effect its
      // one refetch handle.
      refetchRef.current = state.refetch;
      // Ghost, like the toolbar's other icon buttons (export, columns).
      return (
        <Button
          renderIcon={Renew}
          onClick={state.refetch}
          hasIconOnly
          kind="ghost"
          size="lg"
          tooltipPosition="bottom"
          iconDescription={t('common.refresh')}
        />
      );
    },
  };

  return { page };
};
